import { useCallback, useEffect } from "react";
import { modules } from "byzantine";
import { useLocalization } from "@fluent/react";
import {
  DateInput,
  ResponsiveFlex,
  Tag,
  TextInput,
  Tooltip,
  LoadingSkeleton,
  Radio,
} from "@narmi/design_system";
import { Route, Routes, useNavigate } from "react-router-dom";
import { ProgressButtons } from "cerulean";
import { FREQUENCIES } from "byzantine/src/Recurrence";
import { DateTime } from "luxon";

import FrequencySelector from "../FrequencySelector";
import { SudoProvider, SudoRoutes, useSudoContext } from "../../SudoContext";
import { TOTAL_STEPS } from "./AccountSelectScreen";
import HeaderWithSteps from "../../HeaderWithSteps";
import * as formAdapters from "../../../src/adapters";
import styles from "./TransferDetails.module.scss";
import {
  TRANSFER_DETAILS_ROUTE,
  TRANSFER_REVIEW_ROUTE,
  TRANSFERS_ROUTE,
} from "./TransferRoutes";
import { moneyFormatter } from "./TransferReview";
import TransferFeesDialog from "./TransferFeesDialog";
import RequestOTP from "../../SudoContext/RequestOTP";
import SubmitOTP from "../../SudoContext/SubmitOTP";

const DeliverySpeedSelection = () => {
  const form = modules.transfers.TransferForm.useForm();
  const fee = modules.transfers.useCalculateInstantTransferFee();
  const { l10n } = useLocalization();

  function setInstant(value: boolean) {
    if (!!form.values.instant === value) {
      return;
    }

    if (value) {
      form.setFieldValue("frequency", undefined, true);
      form.setFieldValue("date", undefined, true);
      form.setFieldValue("instant", true, true);
      return;
    }

    form.setFieldValue("frequency", FREQUENCIES.ONCE, true);
    form.setFieldValue("date", DateTime.now().toISODate(), true);
    form.setFieldValue("instant", false, true);
  }

  return (
    <div className="padding--bottom--s">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h4 className="fontSize--m nds-sans padding--top--xs padding--bottom--s">
          {l10n.getString("header-delivery-speed")}{" "}
          <Tooltip
            text={l10n.getString("tooltip-scheduled-instant-unavailable")}
          >
            <span className="narmi-icon-info" />
          </Tooltip>
        </h4>
        <TransferFeesDialog />
      </div>
      <div className={styles.deliverySpeedSelectContainer}>
        <Radio
          name="delivery-speed"
          value="standard"
          kind="input-card"
          checked={!form.values.instant}
          onCheck={() => setInstant(false)}
        >
          <div className={styles.radioCardContent}>
            <div className={styles.radioCardHeader}>
              <div className={styles.radioCardHeaderLeft}>
                <p className={styles.radioCardLabel}>
                  {l10n.getString("label-standard")}
                </p>
              </div>
              <p className={styles.radioCardDescription}>
                {l10n.getString("label-standard-delivery-speed-arrival")}
                <span aria-hidden="true"> • </span>
                {l10n.getString("label-standard-delivery-speed-fee")}
              </p>
            </div>
          </div>
        </Radio>
        <Radio
          name="delivery-speed"
          value="instant"
          kind="input-card"
          checked={!!form.values.instant}
          onCheck={() => setInstant(true)}
        >
          <div className={styles.radioCardContent}>
            <div className={styles.radioCardHeader}>
              <div className={styles.radioCardHeaderLeft}>
                <p className={styles.radioCardLabel}>
                  {l10n.getString("label-instant")}
                </p>
                <span className={styles.expediteTagWrap}>
                  <Tag kind="success" label={l10n.getString("tag-expedite")} />
                  <span
                    className={`narmi-icon-zap ${styles.expediteTagIcon}`}
                  />
                </span>
              </div>
              <p className={styles.radioCardDescription}>
                {l10n.getString("label-instant-delivery-speed-arrival")}
                <span aria-hidden="true"> • </span>
                {l10n.getString("label-instant-delivery-speed-fee", {
                  hasFee: fee > 0 ? "true" : "false",
                  fee: moneyFormatter.format(fee),
                })}
              </p>
            </div>
          </div>
        </Radio>
      </div>
    </div>
  );
};

const ScheduledTransferFields = () => {
  const form = modules.transfers.TransferForm.useForm();
  const isRecurring = form.values.frequency !== FREQUENCIES.ONCE;
  const { l10n } = useLocalization();

  function setDate(value: string) {
    form.setFieldValue("date", value, true);
  }

  // we have some minDate logic that sets minDate to next day if frequency is not "once"
  // therefore, we have to also ensure if frequency is not "once", the date fulfills this validation
  useEffect(() => {
    if (isRecurring && form.values.date) {
      const formDate = DateTime.fromISO(form.values.date);
      const tomorrow = DateTime.now().plus({ days: 1 }).startOf("day");
      if (formDate < tomorrow) {
        setDate(tomorrow.toISODate());
      }
    }
  }, [isRecurring]);

  return (
    <div>
      <div className="padding--bottom">
        <DateInput
          minDate={
            !isRecurring
              ? "today"
              : DateTime.now().plus({ days: 1 }).toISODate()
          }
          altFormat={"m/d/Y"}
          altInput
          useIsoOnChange={true}
          label={l10n.getString("label-date")}
          onChange={(val: string) => {
            setDate(val);
          }}
          value={form.values.date}
        />
      </div>
      <FrequencySelector
        label={l10n.getString("label-frequency")}
        {...formAdapters.select(form, "frequency")}
      />
    </div>
  );
};

function TransferDetails() {
  const {
    useIsInstantEligible,
    useIsExternalTransfer,
    useExternalTransferRequiresSudo,
    useShowMemoField,
  } = modules.transfers;
  const { onSetInitialSteps } = useSudoContext();
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const form = modules.transfers.TransferForm.useForm();
  const eligible = useIsInstantEligible();
  const showMemoField = useShowMemoField();
  const requiresSudo =
    useIsExternalTransfer() && useExternalTransferRequiresSudo();
  const { establishSudo } = useSudoContext();

  useEffect(() => {
    onSetInitialSteps(TOTAL_STEPS);
  }, [onSetInitialSteps]);

  const shouldShowScheduling = !eligible || !form.values.instant;

  const handleSubmit = useCallback(() => {
    if (requiresSudo) {
      establishSudo(`/${TRANSFERS_ROUTE}/${TRANSFER_REVIEW_ROUTE}`);
    } else {
      navigate(`/${TRANSFERS_ROUTE}/${TRANSFER_REVIEW_ROUTE}`);
    }
  }, [requiresSudo]);

  useEffect(() => {
    form.submitForm().then(({ success }) => {
      if (!success) {
        navigate(`/${TRANSFERS_ROUTE}`);
      }
    });
  }, []);

  return (
    <ResponsiveFlex gapSize="m">
      <HeaderWithSteps
        headerText={l10n.getString("header-make-a-transfer")}
        step={2}
        totalSteps={TOTAL_STEPS}
      />
      {eligible === undefined && <LoadingSkeleton isLoading lines={2} />}
      {eligible && <DeliverySpeedSelection />}

      <h4 className="fontSize--m nds-sans padding--top--xs padding--bottom--s">
        {l10n.getString("header-transfer-additional-details")}
      </h4>
      {shouldShowScheduling && <ScheduledTransferFields />}
      {showMemoField && (
        <TextInput
          label={l10n.getString("label-memo-optional")}
          endContent={
            form.values.instant ? (
              <Tooltip text={l10n.getString("tooltip-transfers-memo-included")}>
                <span className="narmi-icon-info"></span>
              </Tooltip>
            ) : null
          }
          {...formAdapters.input(form, "memo")}
        />
      )}

      <ProgressButtons
        backLabel={l10n.getString("button-back")}
        nextLabel={l10n.getString("button-next")}
        onBack={() => {
          navigate(`/${TRANSFERS_ROUTE}`);
        }}
        onNext={handleSubmit}
      />
    </ResponsiveFlex>
  );
}

export default function TransferDetailsComponent() {
  return (
    <SudoProvider basename={`/${TRANSFERS_ROUTE}/${TRANSFER_DETAILS_ROUTE}`}>
      <Routes>
        <Route path="/" element={<TransferDetails />} />
        <Route path={`${SudoRoutes.RequestOTP}`} element={<RequestOTP />} />
        <Route path={`${SudoRoutes.SubmitOTP}`} element={<SubmitOTP />} />
      </Routes>
    </SudoProvider>
  );
}
